import { autoScrollForElements } from '@atlaskit/pragmatic-drag-and-drop-auto-scroll/element';
import {
  attachClosestEdge,
  Edge,
  extractClosestEdge,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import {
  BaseEventPayload,
  DragLocationHistory,
  ElementDragPayload,
  ElementDragType,
  Input,
} from '@atlaskit/pragmatic-drag-and-drop/dist/types/internal-types';
import {
  draggable,
  dropTargetForElements,
  monitorForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import ReactDOM from 'react-dom';
// Not used but copied and overridden: TODO, check if this is still necessary
// import { preserveOffsetOnSource } from '@atlaskit/pragmatic-drag-and-drop/element/preserve-offset-on-source';
import { GetOffsetFn } from '@atlaskit/pragmatic-drag-and-drop/dist/types/public-utils/element/custom-native-drag-preview/types';
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview';
import classNames from 'classnames';
import React, {
  CSSProperties,
  forwardRef,
  MouseEventHandler,
  MutableRefObject,
  PropsWithChildren,
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import invariant from 'tiny-invariant';

import Icon from 'components/icons/Icon';
import { IconSize, IconSymbol } from 'components/icons/Icon.types';
import { showDebugPreview } from 'components/layout/DebugPanel';
import { AnyIdObject, AnyOrderObject } from 'helpers/objects';
import {
  isConsideredLeft,
  relativePos,
  subtractPos,
  xyCoord,
  xyPosition,
} from 'helpers/positions';
import { findAncestor, sortByOrder } from 'helpers/sorting';
import { useWebKit } from 'helpers/useOs';
import DropIndicator from './DropIndicator';
import { usePanZoomContext } from './PanZoomContext';
import {
  SortableContextProvider,
  SortablePreviewContext,
  useDraggingState,
  useSortableContext,
} from './SortableContext';
import { SortableItemContextProvider } from './SortableItemContext';

import './Sortable.scss';

export enum SortableListType {
  SortableList = 'SortableList',
  None = 'None',
  Sections = 'Sections',
  Projects = 'Projects',
  Publications = 'Publications',
  Members = 'Members',
  Files = 'Files',
  Folders = 'Folders',
}

export enum SortableItemType {
  SortableItem = 'SortableItem',
  None = 'None',
  Section = 'Section',
  Project = 'Project',
  Publication = 'Publication',
  Member = 'Member',
  File = 'File',
  Folder = 'Folder',
}

export type SortableListInfo = {
  id: number;
  relatedIds?: number[];
  listType: SortableListType;
  items: AnyOrderObject[] | AnyIdObject[];
};

export type SortableInfo = {
  source: ElementDragPayload;
  location: DragLocationHistory;
  dropPosition: xyPosition;
  delta: xyPosition;
};

export type ReorderFunction = (
  sortableInfo: SortableInfo,
  item: AnyOrderObject,
  sourceList: SortableListInfo,
  destination?: AnyOrderObject,
  destinationList?: SortableListInfo,
  copy?: boolean,
  zoomFactor?: number,
) => void;

// This function is copied from pdnd, and corrected: TODO, check if this is still necessary
// it used the container's width and height where it should use the source's
function preserveOffsetOnSource({
  element,
  input,
  extraOffset,
  usesWebKit,
}: {
  element: HTMLElement;
  input: Input;
  extraOffset?: xyCoord;
  usesWebKit?: boolean;
}): GetOffsetFn {
  return ({ container }) => {
    const sourceRect = element.getBoundingClientRect();
    // const containerRect = container.getBoundingClientRect();
    const offsetX = Math.min(
      // difference
      input.clientX - sourceRect.x,
      // don't let the difference be more than the width of the container,
      // otherwise the pointer will be off the preview
      // containerRect.width
      sourceRect.width,
    );
    const offsetY = Math.min(
      // difference
      input.clientY - sourceRect.y,
      // don't let the difference be more than the height of the container,
      // otherwise the pointer will be off the preview
      // containerRect.height
      sourceRect.height,
    );
    const offset = {
      x: offsetX - (extraOffset?.x ?? 0),
      y: offsetY - (extraOffset?.y ?? 0),
    };
    if (usesWebKit) return { x: 0, y: 0 };
    return offset;
  };
}

// TODO: can we remove items from SortableListInfo?
// It currently does not get updated from redux anyway...
// This will be tough, because it is used for rendering
// and other purposes by various components.
type SortableContainerProps = {
  className?: string;
  reorder?: (
    sortableInfo: SortableInfo,
    sourceItem: AnyOrderObject,
    sourceList: SortableListInfo,
    destinationItem?: AnyOrderObject,
    destinationList?: SortableListInfo,
    copy?: boolean,
    zoomFactor?: number,
  ) => void;
  style?: CSSProperties;
  onWheel?: (e: React.WheelEvent) => void;
  onMouseDownCapture?: (e: React.MouseEvent) => void;
  onMouseMoveCapture?: (e: React.MouseEvent) => void;
  onMouseUpCapture?: (e: React.MouseEvent) => void;
  onMouseLeave?: (e: React.MouseEvent) => void;
  onTouchStartCapture?: (e: React.TouchEvent) => void;
  onTouchMoveCapture?: (e: React.TouchEvent) => void;
  onTouchEndCapture?: (e: React.TouchEvent) => void;
};

function sameBlock(source: ElementDragPayload, blockId: number): boolean {
  if (source.data.blockId && blockId) return source.data.blockId === blockId;
  return false;
}

function accepts(
  accepting: SortableItemType[],
  itemType: SortableItemType,
): boolean {
  return accepting.includes(itemType);
}

function dropEFfect(
  props: { input: Input; source: ElementDragPayload },
  blockId?: number,
) {
  const { input, source } = props;
  const effect =
    blockId && !sameBlock(source, blockId ?? 0) && !input.shiftKey
      ? 'copy'
      : 'move';
  return effect;
}

export const SortableContainer = React.forwardRef<
  HTMLDivElement,
  SortableContainerProps & PropsWithChildren
>(function SortableContainer(props, ref) {
  return (
    <SortableContextProvider>
      <SortableContainerWithoutContext {...props} ref={ref} />
    </SortableContextProvider>
  );
});

export const SortableContainerWithoutContext = React.forwardRef<
  HTMLDivElement,
  SortableContainerProps & PropsWithChildren
>(function SortableContainerWithoutContext(props, ref) {
  const {
    className,
    reorder,
    style,
    children,
    onWheel,
    onMouseDownCapture,
    onMouseMoveCapture,
    onMouseUpCapture,
    onMouseLeave,
    onTouchStartCapture,
    onTouchMoveCapture,
    onTouchEndCapture,
  } = props;
  const sortableContext = useSortableContext();
  const panZoomContext = usePanZoomContext();

  // Use a ref to always have the latest reorder function
  const reorderRef = useRef(reorder);
  useEffect(() => {
    reorderRef.current = reorder;
  }, [reorder]);

  const handleDrop = ({
    source,
    location,
  }: {
    source: ElementDragPayload;
    location: DragLocationHistory;
  }) => {
    if (
      location.current.dropTargets.length === 0 ||
      sortableContext.instanceId !==
        location.current.dropTargets[0].data.instanceId
    ) {
      return;
    }
    const sourceList: SortableListInfo = source.data
      .listInfo as SortableListInfo;
    const sourceItem: AnyOrderObject = source.data.item as AnyOrderObject;
    if (sourceItem) {
      // Retrieve the destination list record
      const destinationListRecord =
        location.current.dropTargets[location.current.dropTargets.length - 1];
      const destinationList =
        (destinationListRecord?.data?.listInfo as SortableListInfo) ?? null;
      // and item record
      const destinationItemRecord = location.current.dropTargets[0];
      const destinationItem =
        (destinationItemRecord?.data?.item as AnyOrderObject) ?? null;
      let dropPosition: xyPosition = {
        x: location.current.input.clientX,
        y: location.current.input.clientY,
        relx: 0,
        rely: 0,
      };
      dropPosition =
        relativePos(
          dropPosition,
          destinationListRecord.element as HTMLDivElement,
        ) ?? dropPosition;
      const delta = subtractPos(
        {
          x: location.current.input.clientX, /// zoomFactor,
          y: location.current.input.clientY, /// zoomFactor,
        },
        {
          x: location.initial.input.clientX, /// (zoomFactor),
          y: location.initial.input.clientY, /// (zoomFactor),
        },
      );
      const zoomFactor = panZoomContext?.scale ?? undefined;
      if (reorderRef.current) {
        reorderRef.current(
          { source, location, dropPosition, delta },
          sourceItem,
          sourceList,
          destinationItem,
          destinationList,
          !location.current.input.shiftKey,
          zoomFactor,
        );
      }
    }
  };
  // , [sortableContext, blockId, panZoomContext],
  // );

  useEffect(() => {
    return combine(
      monitorForElements({
        onDrop: handleDrop,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleDrop, reorder]);

  return (
    <div
      className={classNames('sortable_container', className)}
      ref={ref}
      style={style}
      onWheel={onWheel}
      onMouseDownCapture={onMouseDownCapture}
      onMouseMoveCapture={onMouseMoveCapture}
      onMouseUpCapture={onMouseUpCapture}
      onMouseLeave={onMouseLeave}
      onTouchStartCapture={onTouchStartCapture}
      onTouchMoveCapture={onTouchMoveCapture}
      onTouchEndCapture={onTouchEndCapture}
    >
      {children}
    </div>
  );
});

type SortableListProps = {
  listInfo: SortableListInfo;
  accepting?: SortableItemType[];
  className?: string;
  disabled?: boolean;
  horizontal?: boolean;
  copySource?: boolean;
  gap?: number;
  containerRef?: RefObject<HTMLDivElement>;
  blockId?: number;
  isPanning?: boolean;
  style?: CSSProperties;
};

export const SortableList = React.forwardRef<
  HTMLDivElement,
  SortableListProps & PropsWithChildren
>(function SortableList(props, ref) {
  const {
    listInfo,
    className,
    accepting = [SortableItemType.None],
    disabled = false,
    horizontal = false,
    copySource = false,
    gap = 0,
    containerRef,
    blockId,
    isPanning,
    style,
    children,
  } = props;
  const listRef = useRef<HTMLDivElement | null>(null);
  const sortableContext = useSortableContext();
  const { instanceId, overList, overThisList } = sortableContext;

  useEffect(() => {
    if (!listRef) return;
    invariant(listRef.current);
    const cleanUpFns = [
      dropTargetForElements({
        element: listRef.current,
        onDragStart: () => {},
        onDragEnter: (args) => {},
        onDragLeave: (args) => {},
        onDrop: () => {},
        getData: () => {
          return {
            type: listInfo.listType,
            listInfo: listInfo,
            id: listInfo.id,
            instanceId,
          };
        },
        getDropEffect: ({ input, source }) =>
          dropEFfect({ input, source }, blockId),
        getIsSticky: () => false,
        canDrop: ({ input, source }) => {
          if (disabled || copySource) return false;
          return accepts(
            accepting,
            (source.data.type as SortableItemType) ?? SortableItemType.None,
          );
        },
      }),
    ];
    if (containerRef?.current)
      cleanUpFns.push(
        autoScrollForElements({
          element: containerRef.current,
        }),
      );
    return combine(...cleanUpFns);
  }, [
    accepting,
    blockId,
    copySource,
    disabled,
    instanceId,
    listInfo,
    containerRef,
  ]);

  const setRefs = (node: HTMLDivElement | null) => {
    listRef.current = node;
    if (ref) {
      if (typeof ref === 'function') {
        ref(node);
      } else {
        (ref as MutableRefObject<HTMLDivElement | null>).current = node;
      }
    }
  };

  return (
    <div
      className={classNames('sortable_list', className, {
        horizontal: horizontal || undefined,
        draggedOver: overList(listInfo.id),
        draggedOverThis: overThisList(listInfo.id),
        empty: React.Children.count(children) === 0,
        isDisabled: disabled,
      })}
      id={listInfo.id.toString()}
      ref={setRefs}
      style={
        {
          '--gap': `${gap}px`,
        } as CSSProperties
      }
    >
      {children}
    </div>
  );
});

type SortableHandleProps = {
  horizontal?: boolean;
  small?: boolean;
};
const SortableHandle = forwardRef<HTMLDivElement, SortableHandleProps>(
  function (props: SortableHandleProps, ref) {
    const { horizontal, small } = props;
    return (
      <div
        className={classNames('sortable_handle', {
          horizontal: horizontal,
          small: small,
        })}
        ref={ref}
      >
        <Icon
          symbol={horizontal ? IconSymbol.drag4x2 : IconSymbol.drag2x4}
          size={small ? IconSize.smallhandle : IconSize.handle}
          asImg={true}
        />
      </div>
    );
  },
);

type SortableItemProps = {
  listInfo: SortableListInfo;
  item: AnyOrderObject | AnyIdObject;
  itemType: SortableItemType;
  accepting?: SortableItemType[];
  isOnCanvas?: boolean;
  asIcon?: boolean;
  iconOffset?: xyCoord;
  className?: string;
  wrapperClassName?: string;
  addHandle?: boolean;
  smallHandle?: boolean;
  handleRef?: React.RefObject<HTMLDivElement>;
  disabled?: boolean;
  horizontal?: boolean;
  copySource?: boolean;
  noAnimation?: boolean;
  style?: CSSProperties;
  gap?: number;
  containerRef?: RefObject<HTMLDivElement>;
  blockId?: number;
  noDropIndicator?: boolean;
  postDropAnimation?: (itemRef: React.RefObject<HTMLDivElement>) => void;
  onClick?: MouseEventHandler;
  onDoubleClick?: MouseEventHandler;
  onDrag?: (args: BaseEventPayload<ElementDragType>) => void;
  debug?: boolean;
};

export function SortableItem(props: SortableItemProps & PropsWithChildren) {
  return (
    <SortableItemContextProvider id={props.item.id}>
      <SortableItemWithoutContext {...props} />
    </SortableItemContextProvider>
  );
}

export function SortableItemWithoutContext(
  props: SortableItemProps & PropsWithChildren,
) {
  const {
    listInfo,
    item,
    itemType,
    accepting = itemType ? [itemType] : [SortableItemType.None],
    isOnCanvas,
    asIcon,
    iconOffset,
    className,
    wrapperClassName,
    style,
    gap = 0,
    containerRef,
    addHandle = false,
    smallHandle,
    handleRef,
    disabled = false,
    copySource = false,
    horizontal = false,
    postDropAnimation,
    blockId,
    noDropIndicator = false,
    debug,
    children,
  } = props;
  const dragRef = useRef<HTMLDivElement>(null!);
  const dropRef = useRef<HTMLDivElement>(null);
  const internalHandleRef = useRef<HTMLDivElement>(null);
  const [closestEdge, setClosestEdge] = useState<Edge | null>(null);
  const [dragPosition, setDragPosition] = useState<xyPosition | null>(null);

  const sortableContext = useSortableContext();
  const { setTargets, overItem, overThisItem } = sortableContext;
  const sortableContextRef = useRef(sortableContext);
  const { isPreview, isDragging, container, dragged } = useDraggingState();
  // const itemContext = usePossibleSortableItemContext();
  const usesWebKit = useWebKit();

  const getAvailableEdges = useCallback(
    (sourceId: number, horizontal: boolean): Edge[] => {
      const items = sortByOrder((listInfo.items ?? []) as AnyOrderObject[]);
      if (sourceId === item.id) return [];
      const sourceIndex = items.findIndex((i) => i.id === sourceId);
      const targetIndex = items.findIndex((i) => i.id === item.id);
      const edges: Edge[] = [];
      if (sourceIndex === targetIndex - 1)
        edges.push(horizontal ? 'right' : 'bottom');
      else if (sourceIndex === targetIndex + 1)
        edges.push(horizontal ? 'left' : 'top');
      else if (horizontal) edges.push('left', 'right');
      else edges.push('top', 'bottom');
      return edges;
    },
    [item.order, item.id, listInfo.items],
  );

  useEffect(() => {
    sortableContextRef.current = sortableContext;
  }, [sortableContext]);

  useEffect(() => {
    if (!dragRef.current || !dropRef.current) {
      return;
    }
    const cleanUpFns = [
      dropTargetForElements({
        element: dropRef.current,
        getData: ({ input, element, source }) => {
          const data = {
            type: itemType,
            item: item,
            id: item.id,
            instanceId: sortableContextRef.current.instanceId,
          };
          // const edges = getAvailableEdges(source.data.id as number, horizontal);
          return attachClosestEdge(data, {
            input,
            element,
            allowedEdges: horizontal ? ['left', 'right'] : ['top', 'bottom'],
            // allowedEdges: edges,
          });
        },
        getIsSticky: () => false,
        getDropEffect: ({ input, source }) =>
          dropEFfect({ input, source }, blockId),
        onDragEnter: (args) => {
          if (args.source.data.id !== item.id) {
            setClosestEdge(extractClosestEdge(args.self.data));
            setTargets(
              args.location.current.dropTargets.map(
                (dt) => dt.data.id as number,
              ),
            );
          }
        },
        onDrag: (args) => {
          setTargets(
            args.location.current.dropTargets.map((dt) => dt.data.id as number),
          );
          if (args.source.data.id !== item.id)
            setClosestEdge(extractClosestEdge(args.self.data));
        },
        onDragLeave: (args) => {
          setTargets(
            args.location.current.dropTargets.map((dt) => dt.data.id as number),
          );
          setClosestEdge(null);
        },
        onDrop: () => {
          setTargets([]);
          setClosestEdge(null);
        },
        canDrop: ({ input, source }) => {
          if (disabled || copySource) return false;
          return accepts(
            accepting,
            (source.data.type as SortableItemType) ?? SortableItemType.None,
          );
        },
      }),
    ];
    if (!disabled) {
      cleanUpFns.push(
        draggable({
          element:
            handleRef?.current ||
            (addHandle ? internalHandleRef.current : dragRef.current) ||
            dragRef.current,
          canDrag() {
            return true;
          },
          getInitialData: () => ({
            type: itemType,
            id: item.id,
            item: item,
            listInfo: listInfo,
            instanceId: sortableContextRef.current.instanceId,
            blockId,
          }),
          onGenerateDragPreview({ source, location, nativeSetDragImage }) {
            const sortable =
              handleRef?.current || addHandle
                ? (findAncestor(source.element, 'sortable') ?? source.element)
                : source.element;
            const offset = preserveOffsetOnSource({
              element: sortable ?? source.element,
              input: location.current.input,
              // extraOffset: iconOffset,
              usesWebKit,
            });
            if (addHandle)
              setCustomNativeDragPreview({
                nativeSetDragImage,
                getOffset: () => {
                  return { x: 16, y: 16 };
                },
                render({ container }) {
                  sortableContextRef.current.setDraggableState({
                    type: 'preview',
                    container,
                    dragged: sortable,
                    id: item.id,
                  });
                  return () => {
                    if (dropRef.current) showDebugPreview(dropRef.current);
                    sortableContextRef.current.setDraggableState({
                      type: 'dragging',
                      id: item.id,
                    });
                  };
                },
              });
            else {
              setCustomNativeDragPreview({
                nativeSetDragImage,
                getOffset: offset,
                render({ container }) {
                  sortableContextRef.current.setDraggableState({
                    type: 'preview',
                    container,
                    dragged: sortable,
                    id: item.id,
                  });
                  if (usesWebKit)
                    // On Safari, add a delay to allow the portal to render
                    // (don't ask, it doesn't work without this...)
                    setTimeout(() => {}, 1000);
                  return () => {
                    if (dropRef.current) showDebugPreview(dropRef.current);
                    sortableContextRef.current.setDraggableState({
                      type: 'dragging',
                      id: item.id,
                    });
                  };
                },
              });
            }
          },
          onDragStart: () => {
            sortableContextRef.current.setDraggableState({
              type: 'dragging',
              id: item.id,
            });
          },
          onDrag: ({ location, source }) => {
            if (containerRef?.current) {
              const dragPos: xyPosition = {
                x: location.current.input.clientX,
                y: location.current.input.clientY,
                relx: 0,
                rely: 0,
              };
              const relDragPos = location.current.dropTargets[1]
                ? relativePos(dragPos, containerRef.current)
                : dragPos;
              setDragPosition(relDragPos);
            }
            if (props.onDrag) {
              props.onDrag({ location, source });
            }
          },
          onDrop: () => {
            sortableContextRef.current.setDraggableState({ type: 'idle' });
            setDragPosition(null);
            setTargets([]);
            if (dragRef.current && postDropAnimation)
              postDropAnimation(dragRef);
          },
        }),
      );
    }
    return combine(...cleanUpFns);
  }, [
    item,
    listInfo,
    itemType,
    blockId,
    addHandle,
    disabled,
    containerRef,
    handleRef,
    postDropAnimation,
    props.onDrag,
  ]);

  function ForPreview() {
    const content = dragged;
    const previewWidth = content ? content.getBoundingClientRect().width : 0;
    const previewHeight = content ? content.getBoundingClientRect().height : 0;
    return (
      <SortablePreviewContext.Provider value={true}>
        <div
          className={classNames('sortable_wrapper sortable_preview', {
            copying: copySource,
            horizontal: horizontal,
          })}
        >
          <div
            className={classNames('sortable', className, {
              isOnCanvas: isOnCanvas,
              preview: true,
              dragging: true,
              noHandle: !addHandle,
              copying: copySource,
              draggingLeft: dragPosition && isConsideredLeft(dragPosition),
              draggingRight: dragPosition && !isConsideredLeft(dragPosition),
            })}
            style={{
              width: previewWidth,
              height: previewHeight,
              ...style,
            }}
          >
            {addHandle ? (
              <SortableHandle
                horizontal={horizontal}
                ref={internalHandleRef}
                small={smallHandle}
              />
            ) : null}
            {children}
          </div>
        </div>
      </SortablePreviewContext.Provider>
    );
  }

  let portal = null;
  if (isPreview && container) {
    portal = ReactDOM.createPortal(<ForPreview />, container);
  }

  return (
    <div
      ref={dropRef}
      className={classNames('sortable_wrapper', wrapperClassName, {
        horizontal: horizontal,
        isOnCanvas: isOnCanvas,
        dragging: isDragging,
      })}
      style={style}
      data-pd-item={item.id}
      data-pd-order={item.farey_order}
    >
      {portal}
      <div
        ref={dragRef}
        className={classNames('sortable', className, {
          isOnCanvas: isOnCanvas,
          dragging: isDragging,
          draggedOver: overItem(item.id),
          draggedOverThis: overThisItem(item.id),
          noHandle: !addHandle && !handleRef?.current,
          // copying: copySource,
          draggingLeft:
            isDragging && dragPosition && isConsideredLeft(dragPosition),
          draggingRight:
            isDragging && dragPosition && !isConsideredLeft(dragPosition),
          isDisabled: disabled,
        })}
        style={style}
      >
        {addHandle && !disabled ? (
          <SortableHandle
            horizontal={horizontal}
            ref={internalHandleRef}
            small={smallHandle}
          />
        ) : null}
        {children}
      </div>
      {!copySource && !noDropIndicator && closestEdge ? (
        <>
          <DropIndicator edge={closestEdge} gap={`${gap}px`} />
        </>
      ) : null}
    </div>
  );
}
